import { Component, computed, EventEmitter, Output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';

// Services
import { AppConfigService } from '@core/services/app-config.service';
import { AuthService } from '@core/services/auth.service';
import { ModuleService } from '@core/services/module.service';
import { SessionService } from '@core/services/session.service';
import { StorageService } from '@core/services/storage.service';
import { TopbarService } from '@core/services/layouts/topbar.service';
import { UiVersionService } from '@core/services/ui-version.service';

// Models
import { UserMenuRow } from '@core/models/layouts/topbar.model';

// Utils
import { Dic } from '@core/utils/dic.utils';

// Lucide
import {
	LucideAngularModule,
	LucideUser,
	LucideKey,
	LucideSettings,
	LucideCuboid,
	LucideAppWindow,
	LucideArrowLeftRight,
	LucideIconData,
	LucideLogOut,
	LucideUserCog,
	LucideCircleUser,
	LucideBookText,
	LucideCircleStop,
} from 'lucide-angular';

/**
 * Contenu du menu profil de la topbar — identité, accès rapides, bascule
 * mandant / application / langue, administration et déconnexion.
 * Les entrées de liste sont décrites en données (`UserMenuRow`) et rendues par un seul gabarit.
 */
@Component({
	selector: 'topbar-user-menu',
	imports: [NgTemplateOutlet, LucideAngularModule],
	templateUrl: './user-menu.html',
	styleUrl: './user-menu.scss',
})
export class TopbarUserMenu {
	protected readonly Dic = Dic;

	@Output() close = new EventEmitter<void>();

	constructor(
		private session: SessionService,
		private module: ModuleService,

		private router: Router,
		private storage: StorageService,
		private authService: AuthService,
		private uiVersion: UiVersionService,

		private topbarService: TopbarService,
		appConfig: AppConfigService,
	) {
		this.language.set(appConfig.language);
	}

	readonly ready = computed(() => {
		const data = this.session.data();
		if (!data) return false;
		return (
			(data.userstatus === undefined || data.userstatus === 0) &&
			(data.passwordupdate === undefined || data.passwordupdate === 'PASSWORD_OK') &&
			(data.generalconditionok === undefined || !!data.generalconditionok) &&
			!!data.fullname
		);
	});

	// ----- En-tête
	readonly displayName = computed(() => this.session.fullName() || '');
	readonly isMember = computed(() => this.session.isMember());
	readonly hasCotisation = computed(() => this.topbarService.hasXType('club-cotisation'));

	readonly userInitials = computed(() => {
		const parts = this.displayName().trim().split(/\s+/).filter(Boolean);
		if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
		if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
		return '?';
	});

	readonly subtitle = computed(() => {
		const data = this.session.data();
		return data?.displayevent || data?.mandant || '';
	});

	// ----- Accès rapides
	readonly icons = { LucideCuboid, LucideKey, LucideUser };

	openModule(xtype: string): void {
		this.module.openModule(xtype);
		this.close.emit();
	}

	// ----- Compte
	readonly accountRows = computed<UserMenuRow[]>(() => {
		const data = this.session.data();
		if (!data) return [];

		const ready = this.ready();
		const rows: UserMenuRow[] = [];

		if (ready && data.generalconditionexists) {
			rows.push({
				label: Dic.generalcondition.text,
				icon: 'fa fa-handshake',
				action: () => this.module.openModule('app-generalcondition'),
			});
		}
		if (data.supportenabled) {
			rows.push({
				label: Dic.support.text,
				icon: 'fa fa-comments',
				action: () => this.module.openModule('idealogic-support'),
			});
		}
		if (data.isadmin && ready && !data.isguestsession && data.mandant) {
			rows.push({
				label: data.mandant,
				icon: LucideSettings,
				action: () => this.module.openModule('myap-mandant'),
			});
		}
		return rows;
	});

	// ----- Mandants
	readonly mandantListOpen = signal(false);

	readonly mandants = computed(() => this.session.data()?.mandants ?? []);
	readonly currentNomdt = computed(() => this.session.data()?.nomdt ?? 0);
	readonly currentMandantLabel = computed(
		() => this.mandants().find((m) => m.nomdt === this.currentNomdt())?.mdt || this.session.data()?.mandant || '',
	);

	toggleMandantList(): void {
		this.mandantListOpen.update((open) => !open);
	}

	changeMandant(noevt: number): void {
		this.topbarService.changeMandant(noevt).subscribe({
			next: () => window.location.reload(),
		});
	}

	// ----- Applications
	readonly apps = computed(() => {
		const apps = this.session.data()?.apps ?? {};
		return Object.keys(apps).map((key) => ({ key, display: apps[key].display }));
	});
	readonly currentApp = computed(() => this.session.data()?.appname || '');

	changeApp(appname: string): void {
		if (appname === this.currentApp()) return;
		this.topbarService.changeApp(appname).subscribe({
			next: () => window.location.reload(),
		});
	}

	// ----- Langue
	readonly language = signal('');

	setLanguage(language: string): void {
		if (language === this.language()) return;
		this.topbarService.setLanguage(language).subscribe({
			next: () => window.location.reload(),
			error: (err) => console.error('Erreur dans le changement de langue: ', err),
		});
	}

	// ----- Administration
	readonly adminRows = computed<UserMenuRow[]>(() => {
		const data = this.session.data();
		if (!data) return [];

		const ready = this.ready();
		const devMode = !!data.devmode;
		const isGuest = !!data.isguest;
		const isGuestSession = !!data.isguestsession;
		const rows: UserMenuRow[] = [];

		if (data.issystem || data.issysadmin) {
			rows.push({
				label: Dic.params.text,
				icon: LucideSettings,
				action: () => this.module.openModule('main-param'),
			});
		}
		if ((data.logins?.length ?? 0) >= 2) {
			rows.push({
				label: Dic.loginas.text,
				icon: LucideCircleUser,
				action: () => this.module.openModule('main-loginas'),
			});
		}
		if (devMode && ready && !isGuestSession && !isGuest) {
			rows.push({
				label: Dic.changeuserprofile.text,
				icon: LucideUserCog,
				action: () => this.module.openModule('main-change-user-profile'),
			});
		}
		rows.push({
			label: Dic.changeinterface.text,
			icon: LucideAppWindow,
			action: () => this.uiVersion.switchToLegacyAndReload(),
		});
		if (this.session.canSwap() && ready) {
			rows.push({
				label: Dic.swapsession.text,
				icon: LucideArrowLeftRight,
				action: () => this.session.swapSession(),
			});
		}
		if (devMode && !isGuest) {
			rows.push({
				label: Dic.reloaddict.text,
				icon: LucideBookText,
				badge: 'dev',
				action: () => this.reloadDict(),
			});
			rows.push({ label: Dic.killalljobs.text, icon: LucideCircleStop, badge: 'dev' });
		}
		return rows;
	});

	reloadDict(): void {
		this.topbarService.reloadDict().subscribe({
			next: () => window.location.reload(),
			error: (err) => console.error('Erreur dans le rechargement du dictionnaire: ', err),
		});
	}

	// ----- Session
	readonly sessionRows = computed<UserMenuRow[]>(() => {
		const data = this.session.data();
		if (!data) return [];

		const isLogged = data.islogged !== undefined ? !!data.islogged : true;
		const rows: UserMenuRow[] = [];

		if (data.isguestsession) {
			rows.push({ label: Dic.logout.text, icon: 'fa fa-window-close' });
		}
		if (isLogged) {
			rows.push({
				label: Dic.logout.text,
				icon: LucideLogOut,
				danger: true,
				action: () => this.logout(),
			});
		}
		return rows;
	});

	logout(): void {
		this.authService.logout().subscribe({
			next: () => this.redirectToLogin(),
			error: () => this.redirectToLogin(),
		});
	}

	private redirectToLogin(): void {
		this.storage.clearLogin();
		this.session.clear();
		this.router.navigate(['login']);
		window.location.reload();
	}

	// ----- Version
	readonly devMode = computed(() => !!this.session.data()?.devmode);
	readonly appVersion = computed(() => this.formatVersion(this.session.data()?.version || ''));

	private formatVersion(v: string): string {
		if (v.length === 14) {
			return `${v.slice(0, 4)}.${v.slice(4, 6)}.${v.slice(6, 8)} ${v.slice(8, 10)}:${v.slice(10, 12)}`;
		}
		if (v.length === 10) {
			return `20${v.slice(0, 2)}.${v.slice(2, 4)}.${v.slice(4, 6)} ${v.slice(6, 8)}:${v.slice(8, 10)}`;
		}
		return v;
	}

	// ----- Gabarit de ligne
	isLucideIcon(icon: UserMenuRow['icon']): icon is LucideIconData {
		return typeof icon !== 'string';
	}

	runRow(row: UserMenuRow): void {
		row.action?.();
		this.close.emit();
	}
}
